/**
 * Fluxo de pareamento do player:
 *
 * 1. Player chama POST /pairing/request com o hardwareId
 * 2. Servidor retorna um código de 6 dígitos
 * 3. Player EXIBE o código na tela para o operador
 * 4. Operador digita o código no painel web (CastIQ Dashboard)
 * 5. Player poleia GET /pairing/:code/status a cada 3s
 * 6. Quando status = 'paired', salva deviceId+deviceToken e inicia operação
 * 7. Player confirma o recebimento via POST /pairing/:code/ack (best-effort, em background) —
 *    só a partir daí o servidor apaga o plaintext do token; uma resposta perdida no passo 6
 *    não é fatal, o próximo poll (a cada 3s) ainda vai trazer o token de volta
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { env } from '@/env'
import { deviceService } from '@/services/device.service'
import { useAuthStore } from '@/store/auth.store'
import { PLAYER_COLORS } from '@/utils/player-theme'

interface Props {
  onPaired: (deviceId: string) => void
}

type Phase = 'requesting' | 'waiting' | 'error'

export function PairingScreen({ onPaired }: Props) {
  const [phase, setPhase] = useState<Phase>('requesting')
  const [code, setCode] = useState<string | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const onPairedRef = useRef(onPaired)
  onPairedRef.current = onPaired

  // Usamos ref para requestCode evitar dependência circular com startPolling
  const requestCodeRef = useRef<(() => Promise<void>) | null>(null)

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
  }, [])

  // Best-effort: confirma ao servidor que o token já foi persistido localmente, para que
  // ele possa apagar o plaintext. Se todas as tentativas falharem o token só fica retido
  // um pouco mais — a tela já avançou, isso não bloqueia o pareamento.
  const acknowledgePairing = useCallback((pairingCode: string) => {
    void (async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await deviceService.acknowledgePairing(pairingCode)
          return
        } catch {
          await new Promise((resolve) => setTimeout(resolve, 1_000 * (attempt + 1)))
        }
      }
    })()
  }, [])

  const startPolling = useCallback(
    (pairingCode: string) => {
      pollRef.current = setInterval(async () => {
        try {
          const res = await deviceService.getPairingStatus(pairingCode)

          if (res.status === 'paired' && res.deviceId && res.deviceToken) {
            stopPolling()
            useAuthStore.getState().setPairedDeviceId(res.deviceId, res.deviceToken)
            acknowledgePairing(pairingCode)
            onPairedRef.current(res.deviceId)
          } else if (res.status === 'paired') {
            // Backend confirmou o pareamento mas não tem mais o token pra entregar — só
            // acontece se uma tentativa anterior já o recebeu e reconheceu (ou, em códigos
            // antigos de antes desta correção, se a única entrega já foi perdida). Não há
            // como progredir a partir daqui: sem visibilidade, o loop ficaria preso pra
            // sempre com a tela "aguardando confirmação".
            stopPolling()
            setPhase('error')
            setErrorMsg('Pareamento perdido — remova este device no painel e parie novamente.')
          } else if (res.status === 'expired') {
            stopPolling()
            // Código expirou — solicitar novo automaticamente via ref
            void requestCodeRef.current?.()
          }
        } catch {
          // Falha de rede — continua tentando
        }
      }, 3_000)
    },
    [stopPolling, acknowledgePairing]
  )

  const requestCode = useCallback(async () => {
    setPhase('requesting')
    setCode(null)
    setErrorMsg(null)
    stopPolling()

    try {
      const hardwareId = useAuthStore.getState().ensureHardwareId()
      const res = await deviceService.requestPairingCode(hardwareId)

      const expiryMs = new Date(res.expiresAt).getTime()
      setCode(res.code)
      setPhase('waiting')

      countdownRef.current = setInterval(() => {
        const secs = Math.max(0, Math.round((expiryMs - Date.now()) / 1000))
        setSecondsLeft(secs)
      }, 1_000)

      startPolling(res.code)
    } catch {
      setPhase('error')
      setErrorMsg('Não foi possível conectar ao servidor. Verifique a rede.')
    }
  }, [stopPolling, startPolling])

  // Mantém a ref sempre atualizada
  requestCodeRef.current = requestCode

  useEffect(() => {
    requestCode()
    return stopPolling
  }, [requestCode, stopPolling])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: PLAYER_COLORS.bgDark,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 40
      }}
    >
      {/* Logo */}
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontSize: 40,
            fontWeight: 700,
            color: PLAYER_COLORS.brand,
            fontFamily: 'monospace',
            letterSpacing: 4
          }}
        >
          CastIQ
        </div>
        <div style={{ color: PLAYER_COLORS.textFaint, fontSize: 13, marginTop: 6 }}>Digital Signage Player</div>
      </div>

      {/* Card principal */}
      <div
        style={{
          background: PLAYER_COLORS.bgCard,
          border: `1px solid ${PLAYER_COLORS.border}`,
          borderRadius: 12,
          padding: '40px 48px',
          width: 400,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24
        }}
      >
        {phase === 'requesting' && (
          <>
            <div style={{ color: PLAYER_COLORS.textFaint, fontSize: 13, fontFamily: 'monospace' }}>
              Conectando ao servidor...
            </div>
            <div
              style={{
                width: 32,
                height: 32,
                border: `3px solid ${PLAYER_COLORS.borderSubtle}`,
                borderTopColor: PLAYER_COLORS.brand,
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }}
            />
          </>
        )}

        {phase === 'waiting' && code && (
          <>
            <div style={{ color: PLAYER_COLORS.textMuted, fontSize: 12, fontFamily: 'monospace', lineHeight: 1.7 }}>
              Acesse o painel de gestão e informe o código abaixo para parear este dispositivo
            </div>

            {/* Código de pareamento */}
            <div
              style={{
                background: PLAYER_COLORS.bgDark,
                border: `1px solid ${PLAYER_COLORS.borderSubtle}`,
                borderRadius: 8,
                padding: '16px 32px',
                letterSpacing: 16,
                fontSize: 48,
                fontWeight: 700,
                fontFamily: 'monospace',
                color: PLAYER_COLORS.brand
              }}
            >
              {code}
            </div>

            {/* Countdown */}
            <div
              style={{
                color: secondsLeft < 60 ? PLAYER_COLORS.textWarning : PLAYER_COLORS.textDim,
                fontSize: 12,
                fontFamily: 'monospace'
              }}
            >
              {secondsLeft > 0 ? `Expira em ${secondsLeft}s` : 'Solicitando novo código...'}
            </div>

            {/* Indicador de aguardo */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: PLAYER_COLORS.textDim,
                fontSize: 12,
                fontFamily: 'monospace'
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: PLAYER_COLORS.brand,
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}
              />
              Aguardando confirmação no painel...
            </div>
          </>
        )}

        {phase === 'error' && (
          <>
            <div style={{ color: PLAYER_COLORS.textError, fontSize: 13, fontFamily: 'monospace' }}>{errorMsg}</div>
            <button
              type="button"
              onClick={() => void requestCode()}
              style={{
                background: PLAYER_COLORS.brand,
                color: PLAYER_COLORS.bgCardAlt,
                border: 'none',
                borderRadius: 6,
                padding: '10px 24px',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'monospace',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: 2
              }}
            >
              Tentar novamente
            </button>
          </>
        )}
      </div>

      <div style={{ color: PLAYER_COLORS.border, fontSize: 11, fontFamily: 'monospace' }}>
        v{env.VITE_PLAYER_RELEASE}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }
      `}</style>
    </div>
  )
}
